import { useInfiniteQuery } from '@tanstack/react-query';
import { getEffects, EffectSearchParams, EffectWithGroup, GroupEffect } from '../api/effects';
import { useLanguageContext } from '../contexts/LanguageContext';

/**
 * Custom hook for effect search
 * Uses TanStack Query for data fetching, caching, and pagination
 */
export const useEffects = (searchParams: EffectSearchParams = {}) => {
  const { language } = useLanguageContext();
  
  // Use infinite query for pagination
  const query = useInfiniteQuery({
    queryKey: ['effects', searchParams, language],
    queryFn: ({ pageParam = 1 }) => 
      getEffects({ ...searchParams, page: pageParam, size: 100, language }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // Calculate if there are more pages based on total count
      const loadedItemsCount = lastPageParam * lastPage.size;
      return loadedItemsCount < lastPage.total ? lastPageParam + 1 : undefined;
    },
    staleTime: 30000, // 30 seconds
  });
  
  // Compute all effects by flattening pages
  const effects = query.data?.pages.flatMap(page => page.effects) ?? [];
  
  // Добавляем premade группы
  const premadeGroups: EffectWithGroup[] = [];
  // Hidden Village Full Buff
  const hiddenVillageBuffIds = [1007, 1008, 1009, 1010, 1011, 1012, 1013];
  const groupName = 'Hidden Village Full Buff';
  const groupTooltip = 'Полный набор бафов Hidden Village (уровень 1):<br/>1007, 1008, 1009, 1010, 1011, 1012, 1013';
  const firstEffect = effects.find(e => e.state_id === 1051);
  if (firstEffect) {
    premadeGroups.push({
      type: 'GROUP',
      name: groupName,
      tooltip: groupTooltip,
      icon_file_name: firstEffect.icon_file_name,
      group_effects: hiddenVillageBuffIds.map(id => ({ state_id: id, level: 4 })),
    });
  }

  // Heal Full Buff (5 lvl)
  const heal5First = effects.find(e => e.state_id === 163404);
  if (heal5First) {
    premadeGroups.push({
      type: 'GROUP',
      name: 'Heal Full Buff (5 lvl)',
      tooltip: 'Полный набор Heal бафов (5 уровень):<br/>163404, 163405, 163406, 163407 (5лв), 2505 (15лв), 163433 (4лв)',
      icon_file_name: heal5First.icon_file_name,
      group_effects: [
        { state_id: 163404, level: 15 },
        { state_id: 163405, level: 15 },
        { state_id: 163406, level: 15 },
        { state_id: 163407, level: 15 },
        { state_id: 2505, level: 40 },
        { state_id: 163433, level: 40 },
      ],
    });
  }
  // Heal Full Buff (10 lvl)
  if (heal5First) {
    premadeGroups.push({
      type: 'GROUP',
      name: 'Heal Full Buff (10 lvl)',
      tooltip: 'Полный набор Heal бафов (10 уровень):<br/>163404, 163405, 163406, 163407 (10лв), 2505 (30лв), 163433 (8лв)',
      icon_file_name: heal5First.icon_file_name,
      group_effects: [
        { state_id: 163404, level: 30 },
        { state_id: 163405, level: 30 },
        { state_id: 163406, level: 30 },
        { state_id: 163407, level: 30 },
        { state_id: 2505, level: 80 },
        { state_id: 163433, level: 80 },
      ],
    });
  }

  // Sham Full Buff (5 lvl)
  const shamFirst = effects.find(e => e.state_id === 13425);
  if (shamFirst) {
    premadeGroups.push({
      type: 'GROUP',
      name: 'Sham Full Buff (5 lvl)',
      tooltip: 'Полный набор Sham бафов (5 уровень):<br/>13425, 13424, 13423',
      icon_file_name: shamFirst.icon_file_name,
      group_effects: [
        { state_id: 13425, level: 90 },
        { state_id: 13424, level: 90 },
        { state_id: 13423, level: 25 }
      ],
    });
    premadeGroups.push({
      type: 'GROUP',
      name: 'Sham Full Buff (10 lvl)',
      tooltip: 'Полный набор Sham бафов (10 уровень):<br/>13425, 13424, 13423',
      icon_file_name: shamFirst.icon_file_name,
      group_effects: [
        { state_id: 13425, level: 175 },
        { state_id: 13424, level: 175 },
        { state_id: 13423, level: 30 }
      ],
    });
  }

  // CM Full Buff (5 lvl)
  const cmFirst = effects.find(e => e.state_id === 2506);
  if (cmFirst) {
    premadeGroups.push({
      type: 'GROUP',
      name: 'CM Full Buff (5 lvl)',
      tooltip: 'Полный набор CM бафов (5 уровень):<br/>2506, 163429',
      icon_file_name: cmFirst.icon_file_name,
      group_effects: [
        { state_id: 2506, level: 10 },
        { state_id: 163429, level: 3 }
      ],
    });
    premadeGroups.push({
      type: 'GROUP',
      name: 'CM Full Buff (10 lvl)',
      tooltip: 'Полный набор CM бафов (10 уровень):<br/>2506, 163429',
      icon_file_name: cmFirst.icon_file_name,
      group_effects: [
        { state_id: 2506, level: 21 },
        { state_id: 163429, level: 6 }
      ],
    });
  }

  // Full Pieces (1 lvl)
  const piecesBuffIds = [9910, 9911, 9912, 9913, 9914, 9915, 9931, 9932, 9933, 9934, 9935, 9936, 9937, 9938, 9939, 9940, 9941, 9942, 9943];
  const piecesFirst = effects.find(e => e.state_id === 9910);
  if (piecesFirst) {
    premadeGroups.push({
      type: 'GROUP',
      name: 'Full Pieces (1 lvl)',
      tooltip: 'Полный набор Pieces (1 уровень):<br/>' + piecesBuffIds.join(', '),
      icon_file_name: piecesFirst.icon_file_name,
      group_effects: piecesBuffIds.map(id => ({ state_id: id, level: 1 })),
    });
  }

  // Full Buff (HV, Heal, Sham, CM, Pieces)
  // Собираем все эффекты из HV, Heal 10, Sham 10, CM 10, Pieces 1
  const hvGroup = premadeGroups.find(g => g.name === 'Hidden Village Full Buff') as GroupEffect | undefined;
  const heal10Group = premadeGroups.find(g => g.name === 'Heal Full Buff (10 lvl)') as GroupEffect | undefined;
  const sham10Group = premadeGroups.find(g => g.name === 'Sham Full Buff (10 lvl)') as GroupEffect | undefined;
  const cm10Group = premadeGroups.find(g => g.name === 'CM Full Buff (10 lvl)') as GroupEffect | undefined;
  const piecesGroup = premadeGroups.find(g => g.name === 'Full Pieces (1 lvl)') as GroupEffect | undefined;
  if (hvGroup && heal10Group && sham10Group && cm10Group && piecesGroup) {
    premadeGroups.push({
      type: 'GROUP',
      name: 'Full Buff (HV, Heal, Sham, CM, Pieces)',
      tooltip: 'Все основные бафы: Hidden Village, Heal 10, Sham 10, CM 10, Pieces 1',
      icon_file_name: hvGroup.icon_file_name,
      group_effects: [
        ...hvGroup.group_effects,
        ...heal10Group.group_effects,
        ...sham10Group.group_effects,
        ...cm10Group.group_effects,
        ...piecesGroup.group_effects,
      ],
    });
  }

  // Итоговый список: сначала группы, потом обычные эффекты
  const allEffects: EffectWithGroup[] = [...premadeGroups, ...effects];
  
  // Calculate pagination info
  const totalItems = query.data?.pages[0]?.total ?? 0;
  const pageSize = query.data?.pages[0]?.size ?? 100;
  const totalPages = Math.ceil(totalItems / pageSize);
  const currentPage = query.data?.pages.length ?? 0;
  
  return {
    ...query,
    effects: allEffects,
    pagination: {
      totalItems,
      pageSize,
      totalPages,
      currentPage,
      loadedItems: allEffects.length
    }
  };
}; 